import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthService } from '../services/authService';
import { getThemeAssets } from '../utils/theme';

const LoginOTP: React.FC = () => {
  const [phone, setPhone] = useState('');
  const [otp, setOTP] = useState(['', '', '', '', '', '']);
  const [isOTPSent, setIsOTPSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Pre-fill phone from URL param (passed from website after claim/add school)
  useEffect(() => {
    const phoneParam = searchParams.get('phone');
    if (phoneParam) setPhone(phoneParam);
  }, []);
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const leftSideRef = useRef<HTMLDivElement>(null);

  // Get theme assets from environment
  const themeAssets = getThemeAssets();

  // Mouse tracking for interactive effects
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (leftSideRef.current) {
        const rect = leftSideRef.current.getBoundingClientRect();
        setMousePosition({
          x: (e.clientX - rect.left) / rect.width,
          y: (e.clientY - rect.top) / rect.height,
        });
      }
    };

    const leftSide = leftSideRef.current;
    if (leftSide) {
      leftSide.addEventListener('mousemove', handleMouseMove);
      return () => leftSide.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  // Focus on first OTP input when OTP is sent
  useEffect(() => {
    if (isOTPSent && otpInputRefs.current[0]) {
      otpInputRefs.current[0].focus();
    }
  }, [isOTPSent]);

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!phone || !/^\d{10}$/.test(phone)) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
      const response = await fetch(`${apiUrl}/auth/send-whatsapp-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send OTP');
      }

      setIsOTPSent(true);
      setSuccessMessage('OTP sent successfully! Please check your WhatsApp.');
    } catch (err: any) {
      setError(err.message || 'Failed to send OTP. Please try again.');
      console.error('Error sending OTP:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPChange = (index: number, value: string) => {
    // Only allow numbers
    if (value && !/^\d+$/.test(value)) {
      return;
    }

    const newOTP = [...otp];
    newOTP[index] = value;
    setOTP(newOTP);

    // Auto-focus next input
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOTP = [...otp];

    for (let i = 0; i < pastedData.length; i++) {
      newOTP[i] = pastedData[i];
    }

    setOTP(newOTP);

    // Focus on the next empty input or the last input
    const nextIndex = Math.min(pastedData.length, 5);
    otpInputRefs.current[nextIndex]?.focus();
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError('Please enter complete 6-digit OTP');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
      const response = await fetch(`${apiUrl}/auth/verify-whatsapp-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone, otp: otpCode }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Invalid OTP');
      }

      if (data.status === 'success' && data.data?.token) {
        // Use AuthService to handle token and user data
        AuthService.setToken(data.data.token, { expires: 7 });
        AuthService.setUserData(data.data.user);

        // Store user info in localStorage for backward compatibility
        localStorage.setItem('crm_user', JSON.stringify(data.data.user));

        // Navigate to dashboard
        navigate('/dashboard');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err: any) {
      setError(err.message || 'OTP verification failed. Please try again.');
      console.error('Error verifying OTP:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setOTP(['', '', '', '', '', '']);
    setError(null);
    setSuccessMessage(null);
    setIsLoading(true);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
      const response = await fetch(`${apiUrl}/auth/send-whatsapp-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to resend OTP');
      setSuccessMessage('OTP resent successfully! Please check your WhatsApp.');
    } catch (err: any) {
      setError(err.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen lg:h-screen w-screen flex flex-col-reverse lg:flex-row m-0 p-0">
      {/* Left Side - Glass Morphism with Background (Bottom on mobile, Left on desktop) */}
      <div ref={leftSideRef} className="w-full h-[100vh] lg:w-1/2 lg:h-screen relative overflow-hidden">
        {/* Background overlay */}
        <div className="absolute inset-0 bg-login-bgLeft"></div>

        {/* Animated Decoration Images */}
        <div
          className="absolute w-24 h-24 transition-transform duration-1000 ease-out opacity-80 hover:opacity-100"
          style={{
            transform: `translate(${mousePosition.x * 60}px, ${mousePosition.y * 40}px) rotate(${mousePosition.x * 10}deg)`,
            top: '10%',
            left: '10%',
          }}
        >
          <img
            src={themeAssets.loginDecoration}
            alt="Decoration"
            className="w-full h-full object-contain drop-shadow-lg"
          />
        </div>
        <div
          className="absolute w-16 h-16 transition-transform duration-800 ease-out opacity-60 hover:opacity-90"
          style={{
            transform: `translate(${mousePosition.x * -40}px, ${mousePosition.y * 60}px) rotate(${mousePosition.y * 15}deg)`,
            top: '65%',
            right: '15%',
          }}
        >
          <img
            src={themeAssets.loginDecoration}
            alt="Decoration"
            className="w-full h-full object-contain drop-shadow-lg"
          />
        </div>
        <div
          className="absolute w-14 h-14 transition-transform duration-1200 ease-out opacity-50 hover:opacity-80"
          style={{
            transform: `translate(${mousePosition.x * 30}px, ${mousePosition.y * -35}px) rotate(${mousePosition.x * -8}deg)`,
            top: '40%',
            left: '75%',
          }}
        >
          <img
            src={themeAssets.loginDecoration}
            alt="Decoration"
            className="w-full h-full object-contain drop-shadow-lg"
          />
        </div>

        {/* Content Container */}
        <div className="relative z-10 h-full flex flex-col justify-center items-center px-12 py-16">
          {/* Icon */}
          <div className="text-center w-full max-w-lg">
            <div className="mb-8 flex justify-center">
              <div className="p-6 bg-white/10 backdrop-blur-lg rounded-full border border-white/20">
                <svg className="w-16 h-16 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
              </div>
            </div>

            {/* Main Heading */}
            <h1 className="text-5xl font-bold mb-4 text-white leading-tight">
              Login with WhatsApp
            </h1>
            <p className="text-xl text-blue-100/90 font-light mb-2">
              Quick and secure authentication
            </p>
            <p className="text-base text-blue-200/80">
              {!isOTPSent
                ? 'Enter your phone number to receive a verification code via WhatsApp'
                : 'Enter the 6-digit code we sent to your WhatsApp'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Form (Top on mobile, Right on desktop) */}
      <div className="w-full lg:w-1/2 lg:h-screen bg-white flex items-center justify-center px-4 py-8 sm:px-6 lg:px-0">
        <div className="w-full max-w-md mx-auto lg:mx-8">
          <div className="">
            <div className="text-center mb-6">
              <img
                src={themeAssets.loginLogo}
                alt={themeAssets.brandName}
                className="h-10 sm:h-12 mx-auto mb-4"
              />
              <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-700 to-gray-900 bg-clip-text text-transparent mb-2">
                {!isOTPSent ? 'Enter Phone Number' : 'Verify OTP'}
              </h1>
              <p className="text-gray-600 text-sm">
                {!isOTPSent
                  ? 'We\'ll send you a verification code'
                  : 'Check your WhatsApp for the code'
                }
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Success Message */}
            {successMessage && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-700 text-sm">{successMessage}</p>
              </div>
            )}

            {/* Phone Number Form */}
            {!isOTPSent && (
              <form onSubmit={handleSendOTP} className="space-y-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="Enter your mobile number"
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                    disabled={isLoading}
                    maxLength={10}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !phone}
                  style={{ backgroundColor: '#25D366' }}
                  className="w-full text-white py-3 px-4 rounded-lg font-medium hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200 shadow-lg flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending OTP...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                      Send OTP via WhatsApp
                    </>
                  )}
                </button>
              </form>
            )}

            {/* OTP Verification Form */}
            {isOTPSent && (
              <form onSubmit={handleVerifyOTP} className="space-y-6">
                <div>
                  <p className="text-sm text-gray-500 text-center mb-4">
                    OTP sent to <span className="font-semibold text-gray-800">+91 {phone}</span>
                  </p>
                  <label className="block text-sm font-medium text-gray-700 mb-4 text-center">
                    Enter 6-Digit OTP
                  </label>
                  <div className="flex justify-center gap-1.5 sm:gap-2" onPaste={handlePaste}>
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => { otpInputRefs.current[index] = el; }}
                        type="text"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOTPChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        className="w-10 h-10 sm:w-12 sm:h-12 text-center text-lg sm:text-xl font-bold border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                        disabled={isLoading}
                      />
                    ))}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || otp.join('').length !== 6}
                  className="w-full bg-brandColor text-white py-3 px-4 rounded-lg font-medium hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200 shadow-lg"
                >
                  {isLoading ? 'Verifying...' : 'Verify & Login'}
                </button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={isLoading}
                    className="text-brandColor hover:text-brand-hover text-sm font-medium transition-colors"
                  >
                    Didn't receive OTP? Resend
                  </button>
                </div>
              </form>
            )}

            {/* Back to Login Link */}
            <div className="mt-6 text-center">
              <button
                onClick={() => navigate('/login')}
                className="text-gray-600 hover:text-gray-800 text-sm transition-colors"
              >
                ← Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginOTP;
